import {
  ActionBar,
  ActionType,
  AnchoredPanelComponent,
  BookmarkablePage,
  DateTimeDisplay,
  DetailView,
  DetailViewsPanel,
  ErrorViewPanel,
  FormValues,
  getResourceLifecycleStatus,
  getValue,
  GroupItem,
  InputFormGroup,
  LabelMetaItem,
  MetaItemSection,
  optimizedRetryOption,
  stateT,
  Status,
  TagsDisplay,
  uniqueGUID,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Link, Spinner, Stack } from "@fluentui/react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { Fields as TagDetailsFields, TagDetails } from "../../components/TagDetails/TagDetails";
import { ConsoleContext } from "../../console/ConsoleContext";
import {
  DetailsPanelId,
  InlineFormId,
  MYSQL_CREATE_ROUTE,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
} from "../../constants/pluginConstants";
import {
  emptyChar,
  MainTargetId,
  metaItemLabelsGap,
  MonochromeIconIds,
  SvgIconIds,
  ttlOneMinCaching,
} from "../../constants/uiConstants";
import { MdsBackup } from "../../gen/clients/mchub-azure-api-client-mds";
import { buildId, parseId } from "../../helpers/idHelper";
import {
  getBackupTypeStr,
  getCreationTypeStr,
  getStatusInfo,
  getTitleSuffix,
  ResourceStatus,
} from "../../helpers/resourceHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useSidePanel } from "../../hooks/useSidePanel";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { MysqlBackupEditTagsProps, newMysqlBackupEditTags } from "../../operations/MysqlBackup/MysqlBackupEditTags";
import {
  MysqlBackupUpdateTagsProps,
  newMysqlBackupUpdateTags,
} from "../../operations/MysqlBackup/MysqlBackupUpdateTags";
import { MysqlDeleteBackupProps, newMysqlDeleteBackup } from "../../operations/MysqlBackup/MysqlDeleteBackup";
import { getAzureLocationName, getOciRegion } from "../../utils";

export enum ActionIds {
  Delete = "delete",
  Refresh = "refresh",
  Restore = "restore",
  EditTags = "edit-tags",
}

export enum ActionBarTestIds {
  Delete = "delete",
  Refresh = "refresh",
  Restore = "restore",
}

export enum MetaItemSectionTestIds {
  RESOURCE_GROUP = "mis-resource-group",
  STATUS = "mis-status",
  LOCATION = "mis-loaction",
  SUBSCRIPTION = "mis-subscription",
  SUBSCRIPTION_ID = "mis-subscription-id",
  TAGS = "mis-tags",
  BACKUP_TYPE = "mis-backup-type",
  CREATION_TYPE = "mis-creation-type",
  MYSQL_VERSION = "mis-mysql-version",
  SHAPE = "mis-shape",
  BACKUP_SIZE = "mis-backup-size",
  DATA_STORAGE = "mis-data-storage",
  RETENTION_DAYS = "mis-retention-days",
  CREATED_TIME = "mis-created-time",
  UPDATED_TIME = "mis-updated-time",
}

export enum MetaItemActionTestIds {
  EditTags = "edit-tags"
}

export enum LinkTestIds {
  AddTags = "add-tags-link"
}

const registrationIds = PageRegistrationConfig[PageId.MYSQL_BACKUP_DETAILS].map(config => config.key);

const getViewId = (panelId: string | undefined): string => {
  switch (panelId) {
    case DetailsPanelId.TAGS:
      return registrationIds[1];
    case DetailsPanelId.OVERVIEW:
    default:
      return registrationIds[0];
  }
};

export const MysqlBackupDetailsPage = (): JSX.Element => {
  const
    {
      subscriptionId,
      resourceGroup: resGroupFromUrl,
      provider,
      resourceType,
      resourceName: resourceNameFromUrl,
      panelId,
    } = useParams();
  const [searchParams] = useSearchParams();

  const resGroup = decodeURIComponent(resGroupFromUrl || "");
  const resourceName = decodeURIComponent(resourceNameFromUrl || "");
  const location = searchParams.get("location") || "";

  const { trackActionClick, trackActionDiscard, trackFormSave } = useAnalytics();

  const { back, navigateToSelf, navigateToPanel, navigateTo, customData } = useNavigation(ConsoleContext);

  const panelRef = React.useRef<AnchoredPanelComponent>();
  const setPanelRef = (panel: AnchoredPanelComponent): void => {
    panelRef.current = panel;
  };

  const [tagTabKey, setTagTabKey] = React.useState<string>(uniqueGUID());
  const mdsBackupId = buildId({
    subscriptionId: subscriptionId || "",
    resourceGroup: resGroup,
    provider: provider || "",
    resourceType: resourceType || "",
    resourceName,
  });

  React.useEffect(() => {
    // In case page was navigated to directly by entring its URL in the browser
    const registrationConfig = PageRegistrationConfig[PageId.MYSQL_BACKUP_DETAILS].find(
      config => config.panelPath === panelId,
    );
    const urlQueryParams = searchParams.toString() ? `?${searchParams.toString()}` : "";
    const path = `${RESOURCE_ROUTE}/${mdsBackupId}/${panelId}${urlQueryParams}`;
    navigateToSelf(path, registrationConfig?.key || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscription
  const { loading: subscriptionLoading, subscriptions } = useSubscriptions();
  const subscription = !subscriptionLoading && subscriptions
    ? subscriptions.find(s => s.id === subscriptionId)
    : undefined;

  const {
    response,
    loading,
    refresh,
    error: errorMdsBackup,
  } = useQueryCall({
    method: apiClients.withRegion(getOciRegion(location)).mdsDatabaseApi.getMdsBackup,
    options: {
      args: {
        subscriptionId: subscriptionId || "",
        resourceGroupName: resGroup,
        backupKey: resourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadMysqlBackupDetail(resourceName || ""),
      },
      excludeErrorStatus: [404],
    },
  });

  const instance : MdsBackup | undefined = response?.data;
  const isLoading = loading && !instance;

  React.useEffect(() => {
    setTagTabKey(uniqueGUID);
  }, [instance]);

  const onEditTags = (): void => {
    const editTagsProps = {
      targetId: MainTargetId,
      backupId: mdsBackupId,
      defaultTags: tagsDefaultValue,
      location,
      onExecute: refresh,
      onCancel: () => trackActionDiscard(ActionIds.EditTags, PageId.MYSQL_BACKUP_DETAILS, DetailsPanelId.OVERVIEW),
    };
    triggerEditBackupTags(editTagsProps);
  };

  // Tags
  const tagsDefaultValue: {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
  }[] = Object.keys(instance?.freeformTags || {})
    .map((key: string) => ({ name: key, value: instance?.freeformTags ? instance?.freeformTags[key] : "" }));

  const onClose = (): void => back();
  const onMenuItemSelect = (id: string): void => navigateToPanel(id);

  const { trigger: triggerDeleteBackup } = useOperation<MysqlDeleteBackupProps>(newMysqlDeleteBackup);
  const { trigger: triggerEditBackupTags } = useOperation<MysqlBackupEditTagsProps>(newMysqlBackupEditTags);
  const { trigger: triggerUpdateBackupTags } = useOperation<MysqlBackupUpdateTagsProps>(newMysqlBackupUpdateTags);
  const { closePanels } = useSidePanel();

  const overviewActionItems: ActionType[] = [
    // Need to use customData for the dbSystemId since the dbSystemId in the backup is incorrect
    {
      key: ActionIds.Restore,
      testId: ActionBarTestIds.Restore,
      text: Messages.actions.restoreToNewDbSystem(),
      icon: MonochromeIconIds.Database,
      disabled: !customData?.dbSystemId || instance?.status !== ResourceStatus.Active,
      onClick: () => {
        trackActionClick(ActionIds.Restore, PageId.MYSQL_BACKUP_DETAILS, DetailsPanelId.OVERVIEW);
        const { resourceName: dbSystemName } = parseId(customData?.dbSystemId);
        navigateTo(MYSQL_CREATE_ROUTE, PageId.MYSQL_CREATE, {
          subscriptionId,
          dbSystemName,
          backupId: mdsBackupId,
          backupName: instance?.displayName,
          backupDataStorageSizeInGBs: instance?.dataStorageSizeInGBs,
          backupMysqlVersion: instance?.mysqlVersion,
          analytics: {
            pageId: PageId.MYSQL_BACKUP_DETAILS,
            panelId: DetailsPanelId.OVERVIEW,
            actionName: ActionIds.Restore,
          } as NavigationAnalyticsData,
        });
      },
    },
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.MYSQL_BACKUP_DETAILS, DetailsPanelId.OVERVIEW);
        refresh();
      },
    },
    {
      key: ActionIds.Delete,
      testId: ActionBarTestIds.Delete,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      title: Messages.labels.deleteBackup(),
      description: Messages.hints.deleteBackupConfirmation(instance?.displayName || ""),
      onClick: () => {
        trackActionClick(ActionIds.Delete, PageId.MYSQL_BACKUP_DETAILS, DetailsPanelId.OVERVIEW);
      },
      onConfirm: () => {
        triggerDeleteBackup({
          backupId: mdsBackupId,
          location,
          onExecute: () => {
            refresh();
            back();
          },
        });
      },
      onCancel: () => trackActionDiscard(ActionIds.Delete, PageId.MYSQL_BACKUP_DETAILS, DetailsPanelId.OVERVIEW),
    },
  ];

  const essentialsMetaItemGroup: JSX.Element[] = [
    <Stack tokens={{ childrenGap: metaItemLabelsGap }} key="mysql-backup-details-meta-items-left">
      <LabelMetaItem testId={MetaItemSectionTestIds.RESOURCE_GROUP} label={Messages.labels.resourceGroup()}>
        <AzureResourceGroupLink
          resourceId={mdsBackupId}
        />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.STATUS} label={Messages.labels.status()}>
        <Status
          iconPosition="right"
          label={stateT(instance?.status || "")}
          status={getResourceLifecycleStatus(instance?.status || "")}
          statusInfo={
            getStatusInfo(
              instance?.status || "",
              instance?.lastOperationStatus || "",
              instance?.lastOperationStatusDetails || "",
            )
          }
        />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.LOCATION} label={Messages.labels.location()}>
        {getAzureLocationName(instance?.location as string)}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.SUBSCRIPTION} label={Messages.labels.subscription()}>
        <AzureSubscriptionLink
          resourceId={mdsBackupId}
          subscriptionName={subscription?.name as string}
        />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.SUBSCRIPTION_ID} label={Messages.labels.subscriptionId()}>
        {subscriptionId || ""}
      </LabelMetaItem>
      <LabelMetaItem
        testId={MetaItemSectionTestIds.TAGS}
        label={Messages.labels.tags()}
        action={{
          id: ActionIds.EditTags,
          testId: MetaItemActionTestIds.EditTags,
          label: Messages.actions.edit(),
          onClick: () => {
            trackActionClick(ActionIds.EditTags, PageId.MYSQL_BACKUP_DETAILS, DetailsPanelId.OVERVIEW);
            onEditTags();
          },
        }}
      >
        {tagsDefaultValue?.length > 0
          ? (
            <TagsDisplay
              tags={tagsDefaultValue}
            />
          )
          : (
            <Link
              onClick={() => {
                trackActionClick(ActionIds.EditTags, PageId.MYSQL_BACKUP_DETAILS, DetailsPanelId.OVERVIEW);
                onEditTags();
              }}
              data-test-id={LinkTestIds.AddTags}
            >
              {Messages.actions.addTags()}
            </Link>
          )}
      </LabelMetaItem>
    </Stack>,
    <Stack tokens={{ childrenGap: metaItemLabelsGap }} key="mysql-backup-details-meta-items-right">
      <LabelMetaItem testId={MetaItemSectionTestIds.BACKUP_TYPE} label={Messages.labels.type()}>
        {instance?.backupType ? getBackupTypeStr(instance.backupType) : emptyChar}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.CREATION_TYPE} label={Messages.labels.creationType()}>
        {instance?.creationType ? getCreationTypeStr(instance.creationType) : emptyChar}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.MYSQL_VERSION} label={Messages.labels.mySQLVersion()}>
        {instance?.mysqlVersion || emptyChar}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.SHAPE} label={Messages.labels.shape()}>
        {instance?.shapeName || emptyChar}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.BACKUP_SIZE} label={Messages.labels.backupSizeInGB()}>
        {instance?.backupSizeInGBs?.toString() || emptyChar}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.DATA_STORAGE} label={Messages.labels.totalStorageInGB()}>
        {instance?.dataStorageSizeInGBs?.toString() || emptyChar}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.RETENTION_DAYS} label={Messages.labels.retentionDays()}>
        {instance?.retentionInDays?.toString() || emptyChar}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.CREATED_TIME} label={Messages.labels.created()}>
        <DateTimeDisplay date={instance?.timeCreated} />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.UPDATED_TIME} label={Messages.labels.updated()}>
        <DateTimeDisplay date={instance?.timeUpdated} />
      </LabelMetaItem>
    </Stack>,
  ];

  const detailViews: DetailView[] = [
    {
      id: registrationIds[0],
      content: (
        <Stack style={{ height: "100%" }} tokens={{ childrenGap: 10 }}>
          <ActionBar actions={overviewActionItems} onActionClick={closePanels} />
          <MetaItemSection labelWidth={140} title={Messages.detailMysqlBackup.overview.sectionTitles.essentials()}>
            {essentialsMetaItemGroup}
          </MetaItemSection>
        </Stack>
      ),
    },
    {
      id: registrationIds[1],
      content: (
        <TagDetails
          key={tagTabKey}
          tagsDefaultValue={tagsDefaultValue}
          onApply={(formValues: FormValues) => {
            trackFormSave(InlineFormId.TAGS, PageId.MYSQL_BACKUP_DETAILS, DetailsPanelId.TAGS);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const tags: { name: string; value: any; }[] = getValue(formValues, TagDetailsFields.Tags, InputFormGroup);
            // *** Do not call refresh when updating tags on the tags tab toa void resetting user input ***
            const updateTagsProps = {
              backupId: mdsBackupId,
              tags,
              location,
            };
            triggerUpdateBackupTags(updateTagsProps);
          }}
        />
      ),
    },
  ];

  const groupItems: GroupItem[] = [
    {
      items: [
        {
          icon: SvgIconIds.backupsSvg,
          id: detailViews[0].id,
          name: Messages.labels.overview(),
        },
        {
          icon: SvgIconIds.tagsSvg,
          id: detailViews[1].id,
          name: Messages.labels.tags(),
        },
      ],
    },
  ];

  const renderedContent = (): JSX.Element => {
    if (isLoading) {
      return <Spinner label={Messages.common.loading()} />;
    } if (errorMdsBackup) {
      const isNotFound = errorMdsBackup?.status === 404;
      const errorTitle = isNotFound
        ? Messages.detailMysqlBackup.loadError.notFound.title()
        : Messages.detailMysqlBackup.loadError.general.title();
      const errorDetail = isNotFound
        ? Messages.detailMysqlBackup.loadError.notFound.details()
        : Messages.detailMysqlBackup.loadError.general.details();

      return (
        <ErrorViewPanel
          anchorId="page"
          icon={SvgIconIds.backupsSvg}
          title={resourceName}
          errorTitle={errorTitle}
          resourceId={mdsBackupId}
          errorCode={errorMdsBackup?.status}
          details={[
            errorDetail,
            Messages.notifications.apiErrorMsg(errorMdsBackup?.body?.message),
          ]}
          isOpen
          onClose={onClose}
        />
      );
    }
    return (
      <DetailViewsPanel
        anchorId="page"
        componentRef={setPanelRef}
        title={instance?.displayName || ""}
        subTitle={Messages.detailMysqlBackup.titles.long()}
        icon={SvgIconIds.backupsSvg}
        onClose={onClose}
        isOpen
        views={detailViews}
        activeViewId={getViewId(panelId)}
        onMenuItemSelect={onMenuItemSelect}
        menu={groupItems}
        status={instance?.status !== ResourceStatus.Active ? {
          status: getResourceLifecycleStatus(instance?.status || ""),
          tooltip: stateT(instance?.status || ""),
        } : undefined}
      />
    );
  };

  return (
    <BookmarkablePage
      appContext={ConsoleContext}
      registrationIds={registrationIds}
      title={instance?.displayName || Messages.detailMysqlBackup.titles.short()}
      titleSuffix={instance?.displayName ? getTitleSuffix(panelId || "") : undefined}
    >
      {renderedContent()}
    </BookmarkablePage>
  );
};
